import { signal } from '@angular/core';

import { EstadoGravacao } from '../models/estado-gravacao.model';
import { DURACAO_MINIMA_GRAVACAO_MS, FORMATOS_GRAVACAO, INTERVALO_AMOSTRA_GRAVACAO_MS, QTD_BARRAS_GRAVACAO } from './conversa-rodape.constantes';

export class GravadorAudio {
  readonly estado = signal<EstadoGravacao>('parado');
  readonly duracaoMs = signal(0);
  readonly niveis = signal<number[]>([]);
  readonly urlPrevia = signal<string | null>(null);

  private recorder?: MediaRecorder;
  private stream?: MediaStream;
  private contexto?: AudioContext;
  private analisador?: AnalyserNode;
  private chunks: Blob[] = [];
  private formato = FORMATOS_GRAVACAO[0];
  private inicioTrecho = 0;
  private acumuladoMs = 0;
  private timer?: ReturnType<typeof setInterval>;

  async iniciar(): Promise<string | null> {
    if (this.estado() !== 'parado') return null;
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      return 'Seu navegador não suporta gravação de áudio.';
    }
    const formato = FORMATOS_GRAVACAO.find(f => MediaRecorder.isTypeSupported(f.mime));
    if (!formato) return 'Seu navegador não suporta nenhum formato de áudio compatível.';

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      return 'Permita o acesso ao microfone para gravar áudio.';
    }

    this.formato = formato;
    this.chunks = [];
    this.acumuladoMs = 0;
    this.niveis.set([]);
    this.limparPrevia();

    this.recorder = new MediaRecorder(this.stream, { mimeType: formato.mime });
    this.recorder.ondataavailable = e => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };
    this.recorder.start(250);
    this.iniciarAnalisador(this.stream);

    this.inicioTrecho = Date.now();
    this.estado.set('gravando');
    this.timer = setInterval(() => this.tick(), INTERVALO_AMOSTRA_GRAVACAO_MS);
    return null;
  }

  pausar(): void {
    if (this.estado() !== 'gravando' || !this.recorder) return;
    this.recorder.pause();
    this.recorder.requestData();
    this.acumuladoMs += Date.now() - this.inicioTrecho;
    this.duracaoMs.set(this.acumuladoMs);
    this.estado.set('pausado');
    // o requestData entrega o último pedaço logo em seguida; a prévia é montada depois dele
    setTimeout(() => this.montarPrevia(), 120);
  }

  retomar(): void {
    if (this.estado() !== 'pausado' || !this.recorder) return;
    this.limparPrevia();
    this.recorder.resume();
    this.inicioTrecho = Date.now();
    this.estado.set('gravando');
  }

  finalizar(): Promise<File | null> {
    const recorder = this.recorder;
    if (!recorder || this.estado() === 'parado') return Promise.resolve(null);
    if (this.estado() === 'gravando') this.acumuladoMs += Date.now() - this.inicioTrecho;
    const duracao = this.acumuladoMs;

    return new Promise(resolve => {
      recorder.onstop = () => {
        const blob = new Blob(this.chunks, { type: this.formato.mime.split(';')[0] });
        const extensao = this.formato.extensao;
        this.encerrar();
        if (duracao < DURACAO_MINIMA_GRAVACAO_MS || blob.size === 0) {
          resolve(null);
          return;
        }
        resolve(new File([blob], `audio.${extensao}`, { type: blob.type }));
      };
      recorder.stop();
    });
  }

  descartar(): void {
    if (this.recorder && this.recorder.state !== 'inactive') {
      this.recorder.onstop = null;
      this.recorder.stop();
    }
    this.encerrar();
  }

  private tick(): void {
    if (this.estado() !== 'gravando') return;
    this.duracaoMs.set(this.acumuladoMs + (Date.now() - this.inicioTrecho));
    const nivel = this.lerNivel();
    const niveis = [...this.niveis(), nivel];
    this.niveis.set(niveis.length > QTD_BARRAS_GRAVACAO ? niveis.slice(niveis.length - QTD_BARRAS_GRAVACAO) : niveis);
  }

  private iniciarAnalisador(stream: MediaStream): void {
    try {
      const Contexto = window.AudioContext || (window as any).webkitAudioContext;
      this.contexto = new Contexto();
      this.analisador = this.contexto.createAnalyser();
      this.analisador.fftSize = 512;
      this.contexto.createMediaStreamSource(stream).connect(this.analisador);
    } catch {
      this.analisador = undefined;
    }
  }

  private lerNivel(): number {
    if (!this.analisador) return 0.3;
    const dados = new Uint8Array(this.analisador.fftSize);
    this.analisador.getByteTimeDomainData(dados);
    let soma = 0;
    for (const v of dados) {
      const n = (v - 128) / 128;
      soma += n * n;
    }
    const rms = Math.sqrt(soma / dados.length);
    return Math.min(1, Math.max(0.08, rms * 4));
  }

  private montarPrevia(): void {
    if (this.estado() !== 'pausado' || !this.chunks.length) return;
    this.limparPrevia();
    this.urlPrevia.set(URL.createObjectURL(new Blob(this.chunks, { type: this.formato.mime.split(';')[0] })));
  }

  private limparPrevia(): void {
    const url = this.urlPrevia();
    if (url) URL.revokeObjectURL(url);
    this.urlPrevia.set(null);
  }

  private encerrar(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
    this.stream?.getTracks().forEach(t => t.stop());
    this.stream = undefined;
    this.contexto?.close().catch(() => { /* já fechado */ });
    this.contexto = undefined;
    this.analisador = undefined;
    this.recorder = undefined;
    this.chunks = [];
    this.acumuladoMs = 0;
    this.limparPrevia();
    this.niveis.set([]);
    this.duracaoMs.set(0);
    this.estado.set('parado');
  }
}
